/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  AgencyRate,
  AgencyRateOwner,
  AgencyRateOwner as AgencyRateOwnerAPI,
  Building,
  cleanInputCreate,
  cleanInputUpdate,
  CreateAgencyRateInput,
  CreateAgencyRateMutationVariables,
  CreateAgencyRateOwnerInput,
  CreateAgencyRateOwnerMutationVariables,
  createAgencyRate as createMutation,
  createAgencyRateOwner as createOwnerMutation,
  DateTime,
  DeleteAgencyRateOwnerMutationVariables,
  deleteAgencyRate as deleteMutation,
  deleteAgencyRateOwner as deleteOwnerMutation,
  getAgencyRate as getAgencyRateQuery,
  getBuilding as getBuildingQuery,
  getDateTimeDifference,
  getOwnerIdsCoveredByAgencyRate,
  getReadId,
  getTableClientId,
  getUnit as getUnitQuery,
  isCollectedRentAgencyRate,
  syncAgencyRateOwners,
  syncAgencyRates,
  todayDateTime,
  Unit,
  UpdateAgencyRateInput,
  updateAgencyRate as updateMutation,
} from '@rentguru/commons-utils';
import {
  deleteAndHideEntity,
  get,
  list,
  mutation,
  mutationWithFetchBefore,
  NUMBER_OF_MINUTES_FOR_REFETCH,
} from '@up2rent/graphql';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import React, { Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useBuildings } from './BuildingsContext';
import { useLeases } from './LeasesContext';
import { useReconciliationSettings } from './ReconciliationSettingsContext';
import { useUnits } from './UnitsContext';
import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

export const getAgencyRate = async (id: string) => {
  const result = await get<AgencyRate>(getAgencyRateQuery, id);
  return result;
};

export interface AgencyRatesContext extends AgencyRatesState {
  setFetchAgencyRates: () => void;
  getAgencyRate: (id: string) => AgencyRate | null | undefined;
  createAgencyRate: (input: Omit<CreateAgencyRateInput, 'clientId' | 'readId'>) => Promise<AgencyRate>;
  updateAgencyRate: (updates: UpdateAgencyRateInput) => Promise<AgencyRate>;
  deleteAgencyRate: (agencyRate: AgencyRate) => Promise<AgencyRate | null>;
  getAgencyRateOwner: (id: string) => AgencyRateOwner | null | undefined;
  getAgencyRateOwnersOfOwner: (ownerId: string) => AgencyRateOwner[] | null | undefined;
  createAgencyRateOwner: (input: Omit<CreateAgencyRateOwnerInput, 'clientId' | 'readId'>) => Promise<AgencyRateOwner>;
  deleteAgencyRateOwner: (agencyRateOwner: AgencyRateOwner) => Promise<AgencyRateOwner | null>;
}

interface AgencyRatesState {
  loading: boolean;
  error: string | null;
  shouldFetchAgencyRates: boolean;
  agencyRates: AgencyRate[] | null;
  agencyRateOwners: AgencyRateOwner[] | null;
  lastFetchForAgencyRates: DateTime | null;
}

type Action =
  | {
      type: 'SHOULD_FETCH' | 'IS_FETCHING';
    }
  | {
      type: 'FETCH_FAILED';
    }
  | {
      type: 'FETCHED';

      payload: { agencyRates: AgencyRate[]; agencyRateOwners: AgencyRateOwner[] };
    }
  | {
      type: 'ADD_AGENCYRATE' | 'UPDATE_AGENCYRATE';

      payload: { agencyRate: AgencyRate };
    }
  | {
      type: 'ADD_AGENCYRATE_OWNER';

      payload: { agencyRateOwner: AgencyRateOwner };
    }
  | {
      type: 'DELETE_AGENCYRATE';

      payload: { id: string };
    }
  | {
      type: 'DELETE_AGENCYRATE_OWNER';

      payload: { id: string };
    };

const initialState: AgencyRatesState = {
  loading: false,
  error: null,
  shouldFetchAgencyRates: false,
  agencyRates: null,
  agencyRateOwners: null,
  lastFetchForAgencyRates: null,
};

const agencyRatesReducer = (state: AgencyRatesState, action: Action): AgencyRatesState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      // Check //lastFetchForAgencyRates
      if (
        state.loading ||
        state.shouldFetchAgencyRates ||
        (state.lastFetchForAgencyRates &&
          getDateTimeDifference({ start: state.lastFetchForAgencyRates, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetchAgencyRates: true,
      };
    case 'IS_FETCHING':
      return {
        ...state,
        loading: true,
      };
    case 'FETCHED':
      // Build List with merged object
      const agencyRateOwners = action.payload.agencyRateOwners;
      const agencyRates = action.payload.agencyRates.reduce((result, currentAgencyRate) => {
        const owners = agencyRateOwners.filter(
          (aro) =>
            (aro as AgencyRateOwnerAPI).agencyRateId &&
            (aro as AgencyRateOwnerAPI).agencyRateId === currentAgencyRate.id
        );
        result.push({ ...currentAgencyRate, owners: owners as AgencyRateOwner[] });
        return result;
      }, [] as AgencyRate[]);
      return {
        ...state,
        loading: false,
        shouldFetchAgencyRates: false,
        agencyRates,
        agencyRateOwners,
        lastFetchForAgencyRates: todayDateTime(),
      };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetchAgencyRates: false };
    case 'ADD_AGENCYRATE':
      if (!state.agencyRates) {
        return state;
      }
      return {
        ...state,
        agencyRates: [...state.agencyRates, { ...action.payload.agencyRate, owners: [] }],
      };
    case 'ADD_AGENCYRATE_OWNER':
      if (!state.agencyRateOwners || !state.agencyRates) {
        return state;
      }
      const agencyRateOwner = action.payload.agencyRateOwner;

      return {
        ...state,
        // Update agencyRate with this owner
        agencyRates: state.agencyRates.map((agencyRate) => {
          if (agencyRate.id === (agencyRateOwner as AgencyRateOwnerAPI).agencyRateId) {
            return {
              ...agencyRate,
              owners: agencyRate.owners ? [...agencyRate.owners, agencyRateOwner] : [agencyRateOwner],
            };
          }
          return agencyRate;
        }),
        // Add him to agency rate owners list
        agencyRateOwners: [...state.agencyRateOwners, agencyRateOwner],
      };
    case 'UPDATE_AGENCYRATE':
      if (!state.agencyRates) {
        return state;
      }
      return {
        ...state,
        agencyRates: state.agencyRates.map((ar) => {
          if (ar.id === action.payload.agencyRate.id) {
            return { ...action.payload.agencyRate, owners: ar.owners };
          }
          return ar;
        }),
        agencyRateOwners: state.agencyRateOwners ? [...state.agencyRateOwners] : null,
      };
    case 'DELETE_AGENCYRATE':
      if (!state.agencyRates) {
        return state;
      }
      return {
        ...state,
        agencyRates: state.agencyRates.filter((ar) => ar.id !== action.payload.id),
        agencyRateOwners: state.agencyRateOwners ? [...state.agencyRateOwners] : null,
      };
    case 'DELETE_AGENCYRATE_OWNER':
      if (!state.agencyRateOwners || !state.agencyRates) {
        return state;
      }
      const actionRateOwnerId = action.payload.id;
      return {
        ...state,
        // Update agencyRate with this owner
        agencyRates: state.agencyRates.map((agencyRate) => {
          if (agencyRate.owners && agencyRate.owners.some((aro) => aro.id === actionRateOwnerId)) {
            return {
              ...agencyRate,
              owners: agencyRate.owners.filter((aro) => aro.id !== actionRateOwnerId),
            };
          }
          return agencyRate;
        }),
        // Add him to agency rate owners list
        agencyRateOwners: state.agencyRateOwners.filter((aro) => aro.id !== actionRateOwnerId),
      };
    default:
      return state;
  }
};

export const AgencyRatesContext = React.createContext<AgencyRatesContext | null>(null);

export const AgencyRatesContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { clientId } = useUser();
  const { agencyRateDelete } = usePermissions();
  const { units } = useUnits();
  const { getBuilding } = useBuildings();
  const { leases } = useLeases();
  const { forceDetailedAllocationModeForOwners } = useReconciliationSettings();
  const [state, dispatch] = useReducer<Reducer<AgencyRatesState, Action>>(agencyRatesReducer, initialState);

  // Runs before the rate-card write: if the safeguard cannot be persisted, the write does not happen either
  const enforceCollectedRentAllocationMode = async (
    agencyRate: Pick<AgencyRate, 'id' | 'feesAppliedTo' | 'unitIds'>,
    agencyRateOwners: AgencyRateOwner[],
    extraOwnerIds: string[] = []
  ): Promise<void> => {
    if (!isCollectedRentAgencyRate(agencyRate)) {
      return;
    }
    const missingUnitIds = compact(agencyRate.unitIds ?? []).filter(
      (unitId) => !units.some((unit) => unit.id === unitId)
    );
    const fetchedUnits = compact(await Promise.all(missingUnitIds.map((unitId) => get<Unit>(getUnitQuery, unitId))));
    const rateUnits = [...units, ...fetchedUnits].filter((unit) => compact(agencyRate.unitIds ?? []).includes(unit.id));
    const freshBuildings = compact(
      await Promise.all(
        uniq(compact(rateUnits.map((unit) => unit.buildingId))).map((buildingId) =>
          get<Building>(getBuildingQuery, buildingId)
        )
      )
    );
    const resolveBuilding = (buildingId: string) =>
      freshBuildings.find((building) => building.id === buildingId) ?? getBuilding(buildingId);
    const coveredOwnerIds = uniq([
      ...getOwnerIdsCoveredByAgencyRate(agencyRate, agencyRateOwners, [...units, ...fetchedUnits], resolveBuilding),
      ...extraOwnerIds,
    ]);
    if (!isEmpty(coveredOwnerIds)) {
      const coveredLeaseIds = leases.reduce<string[]>(
        (acc, lease) => (coveredOwnerIds.includes(lease.invoiceSenderId) ? [...acc, lease.id] : acc),
        []
      );
      await forceDetailedAllocationModeForOwners(coveredOwnerIds, coveredLeaseIds);
    }
  };

  const fetchAgencyRates = async (): Promise<AgencyRate[]> => {
    return await list<AgencyRate>(syncAgencyRates, 'clientId', getTableClientId(clientId!, 'AgencyRate'));
  };
  const fetchAgencyRateOwners = async (): Promise<AgencyRateOwner[]> => {
    return await list<AgencyRateOwner>(
      syncAgencyRateOwners,
      'clientId',
      getTableClientId(clientId!, 'AgencyRateOwner')
    );
  };

  const setFetchAgencyRates = () => {
    dispatch({ type: 'SHOULD_FETCH' });
  };

  useEffect(() => {
    const fetchAndSet = async () => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const result = await fetchAgencyRates();
        const resultOwners = await fetchAgencyRateOwners();
        dispatch({ type: 'FETCHED', payload: { agencyRates: result, agencyRateOwners: resultOwners } });
      } catch (error) {
        console.error('[agencyRates] fetching agency rates failed', error);
        dispatch({ type: 'FETCH_FAILED' });
      }
    };
    if (state.shouldFetchAgencyRates) fetchAndSet();
    // eslint-disable-next-line
  }, [state.shouldFetchAgencyRates]);

  const getAgencyRate = (id: string) => {
    if (state.loading || state.error || !state.agencyRates) {
      return null;
    }
    return state.agencyRates.find((ar) => ar.id === id);
  };

  const createAgencyRate = async (input: Omit<CreateAgencyRateInput, 'clientId' | 'readId'>): Promise<AgencyRate> => {
    await enforceCollectedRentAllocationMode(
      { id: input.id ?? '', feesAppliedTo: input.feesAppliedTo, unitIds: input.unitIds },
      state.agencyRateOwners ?? []
    );
    const result = await mutation<AgencyRate, CreateAgencyRateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateAgencyRateInput),
        readId: getReadId(clientId!, 'AgencyRate'),
        clientId: getTableClientId(clientId!, 'AgencyRate'),
      },
    });
    dispatch({ type: 'ADD_AGENCYRATE', payload: { agencyRate: result } });
    dispatch({ type: 'SHOULD_FETCH' });
    return result;
  };

  const updateAgencyRate = async (updates: UpdateAgencyRateInput): Promise<AgencyRate> => {
    const currentAgencyRate = await get<AgencyRate>(getAgencyRateQuery, updates.id);
    await enforceCollectedRentAllocationMode(
      {
        id: updates.id,
        feesAppliedTo: updates.feesAppliedTo ?? currentAgencyRate.feesAppliedTo,
        unitIds: updates.unitIds ?? currentAgencyRate.unitIds,
      },
      state.agencyRateOwners ?? []
    );
    const result = await mutationWithFetchBefore<AgencyRate>(updates.id, getAgencyRateQuery, updateMutation, {
      input: cleanInputUpdate(updates, false, true),
    });
    dispatch({ type: 'UPDATE_AGENCYRATE', payload: { agencyRate: result } });
    dispatch({ type: 'SHOULD_FETCH' });
    return result;
  };

  const deleteAgencyRate = async (agencyRate: AgencyRate): Promise<AgencyRate | null> => {
    if (!agencyRateDelete) {
      return null;
    }
    const result = await deleteAndHideEntity<AgencyRate>(agencyRate, deleteMutation);
    dispatch({ type: 'DELETE_AGENCYRATE', payload: { id: agencyRate.id } });
    return result;
  };

  const getAgencyRateOwnersOfOwner = (ownerId: string) => {
    if (state.loading || state.error || !state.agencyRateOwners) {
      return null;
    }
    return state.agencyRateOwners.filter((aro) => aro.owner?.id === ownerId);
  };

  const getAgencyRateOwner = (id: string) => {
    if (state.loading || state.error || !state.agencyRateOwners) {
      return null;
    }
    return state.agencyRateOwners.find((ar) => ar.id === id);
  };

  const createAgencyRateOwner = async (
    input: Omit<CreateAgencyRateOwnerInput, 'clientId' | 'readId'>
  ): Promise<AgencyRateOwner> => {
    const agencyRate = await get<AgencyRate>(getAgencyRateQuery, input.agencyRateId);
    if (!isNil(agencyRate)) {
      await enforceCollectedRentAllocationMode(agencyRate, state.agencyRateOwners ?? [], [input.contactId]);
    }
    const result = await mutation<AgencyRateOwner, CreateAgencyRateOwnerMutationVariables>(createOwnerMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateAgencyRateOwnerInput),
        readId: getReadId(clientId!, 'AgencyRateOwner'),
        clientId: getTableClientId(clientId!, 'AgencyRateOwner'),
      },
    });
    dispatch({ type: 'ADD_AGENCYRATE_OWNER', payload: { agencyRateOwner: result } });
    dispatch({ type: 'SHOULD_FETCH' });
    return result;
  };

  const deleteAgencyRateOwner = async (agencyRateOwner: AgencyRateOwner): Promise<AgencyRateOwner | null> => {
    if (!agencyRateDelete) {
      return null;
    }
    const result = await deleteAndHideEntity<AgencyRateOwner, DeleteAgencyRateOwnerMutationVariables>(
      agencyRateOwner,
      deleteOwnerMutation
    );
    dispatch({ type: 'DELETE_AGENCYRATE_OWNER', payload: { id: agencyRateOwner.id } });
    return result;
  };

  const values = useMemo(
    () => ({
      ...state,
      setFetchAgencyRates,
      getAgencyRate,
      createAgencyRate,
      updateAgencyRate,
      deleteAgencyRate,
      getAgencyRateOwnersOfOwner,
      getAgencyRateOwner,
      createAgencyRateOwner,
      deleteAgencyRateOwner,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <AgencyRatesContext.Provider value={values}>{children}</AgencyRatesContext.Provider>;
};

export const useAgencyRates = (): AgencyRatesContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<AgencyRatesContext | null>(AgencyRatesContext);

  if (isNil(context)) {
    throw new Error('`useAgencyRates` hook must be used within a `AgencyRatesContext` component');
  }

  useEffect(() => {
    if (!firstRender.current) context.setFetchAgencyRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context;
};
